import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Calendar, MapPin, Building2, Mail, Users, Clock } from 'lucide-react';
import { useTheme } from '../../components/ui/theme-provider';
import { cn } from '../../lib/utils';
import { ROUTES } from '../../App';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { format } from 'date-fns';
import { Badge } from '../../components/ui/badge';

const PracticeJobDetailPage = ({ user, currentUser }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) return;
      
      try {
        setLoading(true);
        const jobDoc = await getDoc(doc(db, 'job_listings', jobId));
        
        if (!jobDoc.exists()) {
          setError("Job listing not found");
          return;
        }
        
        const jobData = {
          id: jobDoc.id,
          ...jobDoc.data()
        };
        
        // Security check: Only allow access to jobs created by this user
        if (jobData.postedByUid !== user?.uid) {
          setError("You don't have permission to view this job listing");
          return;
        }
        
        setJob(jobData);
      } catch (err) {
        console.error("Error fetching job details:", err);
        setError("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobDetails();
  }, [jobId, user?.uid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-120px)]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(ROUTES.PRACTICE_JOBS)}
            className={cn(
              theme === 'light' ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white'
            )}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Button>
        </div>
        <div className={cn(
          "p-6 rounded-lg text-center",
          theme === 'light' ? 'bg-red-50 text-red-700' : 'bg-red-900/20 text-red-300'
        )}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex-1 p-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(ROUTES.PRACTICE_JOBS)}
          className={cn(
            theme === 'light' ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white'
          )}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Jobs
        </Button>
        <div className="text-center p-10">
          <p className={theme === 'light' ? 'text-gray-600' : 'text-gray-400'}>
            Job listing not found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header with Back Button */}
      <div className="flex flex-col gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.PRACTICE_JOBS)}
          className={cn(
            "self-start mb-2",
            theme === 'light' ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white'
          )}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Job Listings
        </Button>
        
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className={cn(
              "text-3xl font-bold tracking-tight",
              theme === 'light' ? 'text-purple-700' : 'text-white'
            )}>
              {job.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <div className="flex items-center gap-1">
                <Badge variant={job.status === 'active' ? 'success' : 'secondary'}>
                  {job.status === 'active' ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className={cn(
                "flex items-center gap-1 text-sm",
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              )}>
                <Clock className="h-4 w-4" />
                <span>{job.employmentType}</span>
              </div>
              <div className={cn(
                "flex items-center gap-1 text-sm",
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              )}>
                <Calendar className="h-4 w-4" />
                <span>Posted {job.postedAt?.toDate ? format(job.postedAt.toDate(), 'MMM d, yyyy') : 'Recently'}</span>
              </div>
              {job.views > 0 && (
                <div className={cn(
                  "flex items-center gap-1 text-sm",
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  <Users className="h-4 w-4" />
                  <span>{job.views} view{job.views !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate(`${ROUTES.PRACTICE_JOBS}/edit/${job.id}`)} // Update route to match App.js
              className={cn(
                "bg-purple-600 hover:bg-purple-700 text-white"
              )}
            >
              Edit Job
            </Button>
          </div>
        </div>
      </div>

      {/* Job Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Job Information */}
        <div className="lg:col-span-2 space-y-6">
          <div className={cn(
            "p-6 rounded-lg",
            theme === 'light' ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#2A2A3E] border-purple-500/20'
          )}>
            <h2 className={cn(
              "text-xl font-semibold mb-4",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              Job Description
            </h2>
            <div className={cn(
              "prose max-w-none",
              theme === 'light' ? 'prose-slate' : 'prose-invert'
            )}>
              <p className="whitespace-pre-line">{job.description}</p>
            </div>
            
            {job.requirements && (
              <>
                <h2 className={cn(
                  "text-xl font-semibold mt-6 mb-4",
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                )}>
                  Requirements & Qualifications
                </h2>
                <div className={cn(
                  "prose max-w-none",
                  theme === 'light' ? 'prose-slate' : 'prose-invert'
                )}>
                  <p className="whitespace-pre-line">{job.requirements}</p>
                </div>
              </>
            )}
            
            <h2 className={cn(
              "text-xl font-semibold mt-6 mb-4",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              How to Apply
            </h2>
            <div className={cn(
              "prose max-w-none",
              theme === 'light' ? 'prose-slate' : 'prose-invert'
            )}>
              <p className="whitespace-pre-line">{job.applicationInstructions}</p>
            </div>
          </div>
        </div>

        {/* Right Column - Practice Information & Salary */}
        <div className="space-y-6">
          {job.salary && (
            <div className={cn(
              "p-6 rounded-lg",
              theme === 'light' ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#2A2A3E] border-purple-500/20'
            )}>
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Compensation
              </h2>
              <p className={cn(
                "text-lg font-medium",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                {job.salary}
              </p>
            </div>
          )}
          
          <div className={cn(
            "p-6 rounded-lg",
            theme === 'light' ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#2A2A3E] border-purple-500/20'
          )}>
            <h2 className={cn(
              "text-xl font-semibold mb-4",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              Practice Information
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Building2 className={cn(
                  "h-5 w-5 mt-0.5",
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )} />
                <div>
                  <h3 className={cn(
                    "font-medium",
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  )}>
                    {job.practiceInfo?.name || 'Practice Name'}
                  </h3>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className={cn(
                  "h-5 w-5 mt-0.5",
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )} />
                <div className={cn(
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  <div>{job.practiceInfo?.address || ''}</div>
                  <div>{job.practiceInfo?.postcode || ''}</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Mail className={cn(
                  "h-5 w-5 mt-0.5",
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )} />
                <div className={cn(
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  {job.contactEmail}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeJobDetailPage;
